<template>
  <div class="min-h-screen bg-white">
    <!-- 헤더 -->
    <AppHeader title="심사 현황" backTo="/partners/intro" />

    <!-- 메인 콘텐츠 -->
    <main class="max-w-lg mx-auto px-5 pt-6">
      <!-- 로딩 -->
      <div v-if="loading" class="pt-20">
        <div class="flex flex-col items-center">
          <div class="w-10 h-10 border-3 border-slate-200 border-t-blue-600 rounded-full animate-spin mb-4"></div>
          <p class="text-[15px] font-medium text-slate-500">신청 상태를 확인하는 중...</p>
        </div>
      </div>

      <!-- 심사 중 -->
      <template v-else-if="application && application.status === 'pending'">
        <!-- 상태 아이콘 -->
        <section class="pt-10 pb-8 text-center">
          <div class="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <svg class="w-10 h-10 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <h1 class="text-[24px] font-black text-slate-900 tracking-tight mb-2">심사 진행 중</h1>
          <p class="text-[15px] font-medium text-slate-500">파트너 등록 신청이 검토 중입니다</p>
        </section>

        <!-- 신청 정보 카드 -->
        <section class="mb-6">
          <div class="bg-slate-50 rounded-2xl p-5">
            <div class="space-y-4">
              <div class="flex justify-between items-center">
                <span class="text-[13px] font-bold text-slate-500">신청일</span>
                <span class="text-[14px] font-bold text-slate-900">{{ formatDate(application.createdAt) }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-[13px] font-bold text-slate-500">신청 현장</span>
                <span class="text-[14px] font-bold text-slate-900">{{ application.propertyName }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-[13px] font-bold text-slate-500">영업명</span>
                <span class="text-[14px] font-bold text-blue-600">{{ application.displayName }}</span>
              </div>
            </div>
          </div>
        </section>

        <!-- 안내 메시지 -->
        <section class="mb-8">
          <div class="bg-blue-50 rounded-2xl p-5">
            <div class="flex items-start gap-3">
              <div class="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <div class="flex-1">
                <p class="text-[14px] font-bold text-slate-900 mb-1">심사 안내</p>
                <p class="text-[13px] font-medium text-slate-600 leading-relaxed">
                  신청 심사는 1~2영업일 내에 완료됩니다.<br/>
                  승인되면 파트너스센터를 이용하실 수 있습니다.
                </p>
              </div>
            </div>
          </div>
        </section>

        <!-- 로그아웃 버튼 -->
        <button
          @click="logout"
          class="w-full py-4 bg-slate-100 text-slate-700 text-[15px] font-bold rounded-2xl active:scale-[0.98] transition-transform"
        >
          로그아웃
        </button>
      </template>

      <!-- 반려됨 -->
      <template v-else-if="application && application.status === 'rejected'">
        <!-- 상태 아이콘 -->
        <section class="pt-10 pb-8 text-center">
          <div class="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <svg class="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
            </svg>
          </div>
          <h1 class="text-[24px] font-black text-slate-900 tracking-tight mb-2">심사 반려</h1>
          <p class="text-[15px] font-medium text-slate-500">신청이 반려되었습니다</p>
        </section>

        <!-- 반려 사유 -->
        <section class="mb-6">
          <div class="bg-red-50 rounded-2xl p-5">
            <p class="text-[13px] font-bold text-red-600 mb-2">반려 사유</p>
            <p class="text-[14px] font-medium text-red-700 leading-relaxed">{{ application.rejectReason }}</p>
          </div>
        </section>

        <!-- 신청 정보 카드 -->
        <section class="mb-8">
          <div class="bg-slate-50 rounded-2xl p-5">
            <div class="space-y-4">
              <div class="flex justify-between items-center">
                <span class="text-[13px] font-bold text-slate-500">신청일</span>
                <span class="text-[14px] font-bold text-slate-900">{{ formatDate(application.createdAt) }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-[13px] font-bold text-slate-500">반려일</span>
                <span class="text-[14px] font-bold text-red-600">{{ formatDate(application.rejectedAt) }}</span>
              </div>
              <div class="flex justify-between items-center">
                <span class="text-[13px] font-bold text-slate-500">신청 현장</span>
                <span class="text-[14px] font-bold text-slate-900">{{ application.propertyName }}</span>
              </div>
            </div>
          </div>
        </section>

        <!-- 버튼들 -->
        <div class="space-y-3">
          <router-link
            to="/partners/register"
            class="block w-full py-4 bg-blue-600 text-white text-[16px] font-bold rounded-2xl text-center active:scale-[0.98] transition-transform"
          >
            다시 신청하기
          </router-link>
          <button
            @click="logout"
            class="w-full py-4 bg-slate-100 text-slate-700 text-[15px] font-bold rounded-2xl active:scale-[0.98] transition-transform"
          >
            로그아웃
          </button>
        </div>
      </template>

      <!-- 하단 링크 -->
      <section class="mt-10 pb-10">
        <div class="flex justify-center">
          <router-link
            to="/"
            class="text-[13px] font-medium text-slate-400 active:text-slate-600"
          >
            메인으로
          </router-link>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { collection, query, where, getDocs } from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import { useAuth } from '@/composables/common/useAuth'
import AppHeader from '@/components/common/AppHeader.vue'

export default {
  name: 'PartnersStatusView',
  components: {
    AppHeader
  },
  setup() {
    const router = useRouter()
    const { user, loading: authLoading, logout: authLogout } = useAuth()

    const loading = ref(true)
    const application = ref(null)

    const formatDate = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`
    }

    const fetchLatestApplication = async () => {
      // 로그인되지 않은 경우
      if (!user.value) {
        router.push('/partners/login')
        return
      }

      try {
        // 가장 최근 신청 조회 (orderBy 없이 조회 후 클라이언트에서 정렬)
        const q = query(
          collection(db, 'partnerApplications'),
          where('partnerId', '==', user.value.uid)
        )
        const snapshot = await getDocs(q)

        if (!snapshot.empty) {
          // 클라이언트에서 최신순 정렬
          const applications = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          applications.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          application.value = applications[0]

          // 승인된 경우 대시보드로
          if (application.value.status === 'approved') {
            router.push('/partners')
            return
          }
        } else {
          // 신청 내역 없으면 등록 페이지로
          router.push('/partners/register')
          return
        }
      } catch (error) {
        console.error('신청 조회 실패:', error)
      } finally {
        loading.value = false
      }
    }

    const logout = async () => {
      await authLogout()
      router.push('/partners/login')
    }

    // auth 로딩이 완료되면 신청 조회
    watch(authLoading, (isLoading) => {
      if (!isLoading) {
        fetchLatestApplication()
      }
    }, { immediate: true })

    return {
      loading,
      application,
      formatDate,
      logout
    }
  }
}
</script>
